import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable } from 'mobx';
import { UserGroupStore, MembershipTypeStore, MembershipGroupStore, UserStore } from '@seedlang/stores';
import Paginator from 'components/paginator';
import styled from '@emotion/styled';
import { Link } from 'react-router';
import Filter from 'components/backend_filter/filter';
import UserGroupCreate from 'pages/builder/user_groups/user_group_create';
import Modal from 'components/modal';
import { AppUI } from '@seedlang/state';
import { Constants } from '@seedlang/constants';
import Spinner from 'components/spinner';
import autobind from 'autobind-decorator';
import Alert from 'components/alert';
import CheckIfTrue from 'components/check_if_true';
import GrayButton from 'components/button/gray_button';
import PageTitle from 'components/common/page_title';

const Wrapper = styled.div`
  .text {
    padding: 0 5px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
`;

const NotInGroupAlertWrapper = styled.div`
  margin-top: 10px;
`;

const AddToSiteButton = styled.button`
  cursor: pointer;
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  text-decoration: underline;
  color: inherit;
`;

@observer
class UsersIndex extends React.Component {
  @observable showCreate = false;
  @observable showSpinner = false;
  @observable showSuccess = false;
  @observable seedlangUserId = null;
  @observable searchedUsersForEmail = '';

  @computed get exportUrl() {
    const baseUrl = window.location.href.match('ngrok')
      ? 'http://localhost:3000'
      : '';
    return `${baseUrl}/api/user_groups/export?group_id=${AppUI.groupId}`;
  }

  constructor(props) {
    super(props);
    MembershipTypeStore.getIndex();
    MembershipGroupStore.getIndex();
    this.disposer = autorun(() => {
      if (AppUI.userIsLoaded && !AppUI.user.guest && !AppUI.permissions.canAccessBasicUserData) {
        AppUI.routeStore.routeToNamed('creator.worksheets.index');
      }

      if (this.isOnlyFilteringByEmail && this.hasNoResults && this.searchedUsersForEmail !== this.cleanEmail) {
        UserStore.getIndex({
          queryStrings: {
            full_text_search: true,
            email: this.cleanEmail,
          },
        }, usersResult => {
          this.seedlangUserId = usersResult[0]?.id ?? null;
        });
        this.searchedUsersForEmail = this.cleanEmail;
      } else {
        this.seedlangUserId = null;
      }
    });
  }

  componentWillUnmount() {
    this.disposer && this.disposer();
  }

  @computed get membershipTypeOptions() {
    const options = MembershipTypeStore.indexData.map(item => ({id: item.id, name: item.name}));
    options.push({id: 'nil', name: 'No Membership'});
    return options;
  }

  @computed get membershipGroupOptions() {
    const options = MembershipGroupStore.indexData.map(item => ({id: item.id, name: item.name}));
    options.push({id: 'nil', name: 'No Membership Group'});
    return options;
  }

  @autobind addUserToGroup() {
    UserGroupStore.create({
      data: {
        group_id: AppUI.groupId,
        user_id: this.seedlangUserId,
      },
    });
  }

  @computed get isOnlyFilteringByEmail() {
    const activeFilters = Object.keys(UserGroupStore.filters).filter(key => UserGroupStore.filters[key] !== '');
    return activeFilters.length === 1 && activeFilters[0] === 'email';
  }

  @computed get hasNoResults() {
    return !UserGroupStore.hasOutstandingRequests && UserGroupStore.indexData.length === 0;
  }

  @computed get cleanEmail() {
    return decodeURIComponent(UserGroupStore.filters.email?.replaceAll('~', ''));
  }

  render() {
    return (
      <Wrapper>
        <PageTitle>Users</PageTitle>
        {
          this.showSuccess && (
            <Alert>
              Your users have been successfully synchronized with MailerLite.
            </Alert>
          )
        }
        {
          this.showCreate &&
            <Modal
              onCloseModal={() => this.showCreate = false}
            >
              <UserGroupCreate />
            </Modal>
        }
        <Filter
              store={UserGroupStore}
              queryStrings={{include_count: true, expanded: true}}
              defaultSort='email'
              namespace='patreon-user-index'
              ids={{groupId: AppUI.groupId}}
              filters={
                [
                  {
                    type: 'text',
                    name: 'email',
                    label: 'email',
                    placeholder: 'Email',
                    default: '',
                  },
                  {
                    type: 'text',
                    name: 'name',
                    label: 'name',
                    placeholder: 'Name',
                    default: '',
                  },
                  {
                    type: 'select',
                    name: 'membership_type_id',
                    options: this.membershipTypeOptions,
                    labelField: 'name',
                    label: 'Membership Tier',
                    default: '',
                  },
                  {
                    type: 'select',
                    name: 'membership_group_id',
                    options: this.membershipGroupOptions,
                    labelField: 'name',
                    label: 'Membership Group',
                    default: '',
                  },
                  {
                    type: 'boolean',
                    name: 'permanent',
                    label: 'Permanent',
                    default: '',
                  },
                  {
                    type: 'select',
                    name: 'role',
                    options: Constants.USER_ROLES,
                    label: 'Role',
                    placeholder: 'Role',
                    default: '',
                  },
                ]
              }
        />
        <ButtonRow>
          <GrayButton onClick={() => this.showCreate = true}>
            <i className='fa fa-plus' />
            Add User
          </GrayButton>
          {
            AppUI.permissions.canAccessUserEmails && (
              <a href={this.exportUrl} target='_blank' rel='noopener noreferrer'>
                <GrayButton style={{marginLeft: '10px'}}>
                  <i className='fa fa-angle-right' />
                  Export Users Table
                </GrayButton>
              </a>
            )
          }
          {
            this.showSpinner && (
              <div style={{marginLeft: '10px'}}>
                <Spinner background={AppUI.site.accentColor} />
              </div>
            )
          }
        </ButtonRow>
        {
          this.seedlangUserId !== null &&
            <NotInGroupAlertWrapper>
              <Alert>
                <i>{this.cleanEmail}</i> has a Seedlang account but is not a member of {AppUI.site.name}.
                <br />
                <AddToSiteButton onClick={this.addUserToGroup}>Add to {AppUI.site.name}</AddToSiteButton>
              </Alert>
            </NotInGroupAlertWrapper>
        }
        <table
          className='table-wrapper'
          style={{
            marginTop: 10,
          }}
        >
          <thead>
            <tr>
              <th width='40'></th>
              {
                AppUI.permissions.canAccessUserEmails && (
                  <th>Email</th>
                )
              }
              <th>Name</th>
              {
                !AppUI.siteIsDefault &&
                  <th>Membership Type</th>
              }
              {
                AppUI.site.enableMailerlite && (
                  <th>Mailerlite</th>
                )
              }
              <th>Agreed to Email?</th>
            </tr>
          </thead>
          <tbody>
          {
            UserGroupStore.requestCounter > 0 &&
              <tr>
                <td colSpan={6}>
                  <Spinner background={AppUI.site.accentColor} />
                </td>
              </tr>
          }
            {
              UserGroupStore.indexData.map(item => {
                return (
                  <tr key={item.id}>
                    <td>
                      <Link
                        to={{ name: 'creator.users.edit', params: { userId: item.user.id } }}
                        className='on-click'
                      >
                        <i className='fa fa-chevron-right fa-block' />
                      </Link>
                    </td>
                    {
                      AppUI.permissions.canAccessUserEmails && (
                        <td>
                          <div className='text'>
                            {item.user.email}
                          </div>
                        </td>
                      )
                    }
                    <td>
                      <div className='text'>
                        {item.user.name}
                      </div>
                    </td>
                    {
                      !AppUI.siteIsDefault &&
                        <td>
                          <div className='text'>
                            {item.membershipType?.name}
                          </div>
                        </td>
                    }
                    {
                      AppUI.site.enableMailerlite && (
                        <td>
                          {
                            item.mailerliteId &&
                              <a href={`https://dashboard.mailerlite.com/subscribers/${item.mailerliteId}`} className='underline' target='_blank'>Mailerlite Page</a>
                          }
                        </td>
                      )
                    }
                    <td>
                      <CheckIfTrue
                        value={item.userAgreedToEmail === 'true'}
                      />
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
        <Paginator
          store={UserGroupStore}
          queryStrings={{expanded: true, include_count: true}}
        />
      </Wrapper>
    );
  }
}

export default UsersIndex;
